import React from "react";
import { SectionBadge } from "./SectionBadge";
import { RichText } from "./RichText";
import { Title } from "./Title";

export type SectionHeaderProps = {
    badge?: string;
    badgeVariant?: "light" | "dark";
    badgeClassName?: string; // Allow customization of badge styling
    title?: string;
    titleHighlight?: string;
    subtitle?: string;
    variant?: "light" | "dark";
    className?: string;
    titleClassName?: string;
    highlightClassName?: string; // Allow customization of highlight color
    /**
     * Ebene der Ueberschrift (12.8.2026).
     *
     * Standard `2` — eine Sektion ist ueblicherweise ein Abschnitt erster
     * Ordnung unter der `h1` der Seite. Steht die Sektion aber INNERHALB eines
     * Kapitels (`ChapterSection`, Startseite), traegt das Kapitel bereits die
     * `h2` und die Sektion gehoert eine Ebene tiefer. Ohne diesen Schalter
     * stuenden mehrere `h2` im selben Kapitel und die Dokumentgliederung laese
     * sich als ebenso viele Abschnitte — genau der Eindruck, den die
     * Kapitel-Fusion aufloest.
     *
     * Die Groesse folgt der Ebene und ist NICHT ueber `titleClassName`
     * korrigierbar: Ohne `tailwind-merge` gewinnt bei zwei Schriftgrad-Klassen
     * die spaetere im erzeugten Stylesheet, nicht die spaetere im Attribut —
     * `text-4xl` schluege ein mitgegebenes `text-2xl`. Deshalb entscheidet die
     * Ebene, und `titleClassName` bleibt fuer alles andere.
     */
    headingLevel?: 2 | 3;
};

export const SectionHeader: React.FC<SectionHeaderProps> = ({
    badge,
    badgeVariant = "dark",
    badgeClassName = "",
    title,
    titleHighlight,
    subtitle,
    variant = "light",
    className = "",
    titleClassName = "",
    highlightClassName,
    headingLevel = 2,
}) => {
    const hasTextAlign =
        className.includes("text-left") || className.includes("text-right") || className.includes("text-center");
    const Heading = headingLevel === 3 ? "h3" : "h2";
    const headingSize = headingLevel === 3 ? "text-2xl md:text-3xl" : "text-4xl md:text-5xl";
    return (
        <div className={`${hasTextAlign ? "" : "text-center"} mb-16 ${className}`}>
            {badge && (
                <SectionBadge variant={badgeVariant} className={badgeClassName}>
                    {badge}
                </SectionBadge>
            )}
            <Heading
                className={`${headingSize} font-black mt-6 ${variant === "dark" ? "text-white" : "text-black"} ${titleClassName}`}
            >
                <Title title={title} titleHighlight={titleHighlight} highlightClassName={highlightClassName} />
            </Heading>
            {subtitle && (
                /* `text-balance`: Lead-Text, kein Fliesstext — siehe
                   `docs/rules/coding-style.md` → «Zeilenumbruch». */
                <p
                    className={`text-balance text-xl mt-6 max-w-3xl mx-auto ${variant === "dark" ? "text-white/70" : "text-black"}`}
                >
                    <RichText content={subtitle} />
                </p>
            )}
        </div>
    );
};

export default SectionHeader;
